import React, { ComponentProps, ReactNode, useMemo } from "react";
import { Offset, TooltipProps as ReactTooltipProps } from "react-tooltip";
import { v4 as uuidv4 } from "uuid";
import { colors, spacing } from "../../stitches.config";
import { MarkdownContainer } from "./__styles__/MarkdownContainer";
import { StyledTooltip, TooltipWrapper } from "./__styles__/Tooltip";
import { Icon } from "./Icons/LucideIcons";
import { Body } from "./Typography";

export type TooltipProps = Omit<ReactTooltipProps, "offset"> & {
  /**
   * The content that, when hovered or focused, will reveal the tooltip.
   */
  content: string | JSX.Element;
  /**
   * The content that will be displayed in the tooltip.
   */
  popoverContent: ReactNode;
  /**
   * Optional ID to connect the tooltip to the content. A random ID will be
   * generated if not provided.
   */
  id?: string;
  /**
   * Plain text version of the tooltip content for screen readers. Ensure that
   * this prop is provided if the provided `popoverContent` is not a string.
   */
  ariaLabel?: string;
  leftSpace?: boolean;
};

export const Tooltip = ({
  content,
  popoverContent,
  id,
  leftSpace = true,
  ariaLabel,
  ...tooltipProps
}: TooltipProps) => {
  const autoId = useMemo(() => uuidv4(), []);
  const finalId = id ?? autoId;

  let offset: Offset;
  switch (tooltipProps.place) {
    case "right":
      offset = { top: 0 };
      break;
    case "top":
    default:
      offset = { top: -8 };
      break;
  }

  let ariaLabelText = "";
  if (ariaLabel) {
    ariaLabelText = ariaLabel;
  } else if (typeof popoverContent === "string") {
    ariaLabelText = popoverContent;
  }

  const contentWithAriaProps =
    React.isValidElement(content) ?
      React.cloneElement(content as React.ReactElement<any>, {
        "data-for": finalId,
        "data-tip": ariaLabelText,
        "aria-describedby": finalId,
        tabIndex: 0,
      })
    : content;

  return (
    <TooltipWrapper leftSpace={leftSpace}>
      {contentWithAriaProps}
      <StyledTooltip
        id={finalId}
        clickable={true} // allows for markdown tooltips to have links in them
        delayHide={150}
        backgroundColor={colors.bgUiInteractionInverted.value}
        border={false}
        effect="solid"
        offset={offset}
        {...tooltipProps}
        arrowColor="transparent"
        role="tooltip"
      >
        <Body
          size="small"
          type="regular"
          color="contentPrimaryInverted"
          style={{
            maxWidth: "200px",
            whiteSpace: "normal",
            textAlign: "left",
            margin: 0,
          }}
          as="div"
        >
          {popoverContent}
        </Body>
      </StyledTooltip>
    </TooltipWrapper>
  );
};

type InformationalTooltipProps = Omit<
  TooltipProps,
  "content" | "popoverContent"
> & {
  iconProps?: Partial<ComponentProps<typeof Icon>>;
  tooltipText: ReactNode;
};

export const InformationalTooltip = ({
  iconProps,
  tooltipText,
  ...props
}: InformationalTooltipProps) => {
  const content = (
    <span style={{ position: "relative", top: spacing["2xs"].value }}>
      {/* Need to add this unicode character (word-joiner)
          to prevent the tooltip from being cut off */}
      &#8288;
      <Icon
        aria-label="Information"
        iconName="info"
        color="contentSecondary"
        size={14}
        cursor="pointer"
        {...iconProps}
      />
    </span>
  );

  return <Tooltip {...props} content={content} popoverContent={tooltipText} />;
};

export const MarkdownTooltip: React.FC<
  Omit<ComponentProps<typeof InformationalTooltip>, "tooltipText"> & {
    tooltipText: string;
  }
> = ({ tooltipText, ...props }) => {
  const tooltip = (
    <MarkdownContainer
      tooltip
      markdown={tooltipText}
      options={{
        openLinksInNewWindow: true,
      }}
    />
  );

  return (
    <InformationalTooltip
      {...props}
      tooltipText={tooltip}
      // since the tooltip is markdown, it could have links, and so
      // we want to delay the hide by 150ms to allow for links to be clicked
      delayHide={150}
      clickable={true}
    />
  );
};
